import axios from 'axios';

/**
 * Vuex module for the BOOST-FEATURED carousel widget.
 *
 * Single responsibility: fetch + expose the list of admin-whitelisted
 * events with an active BoostOdds rule, as returned by
 *   POST /v3/events/mixed/boost-featured
 *
 * Kept intentionally SEPARATE from the `featuredEvents` module even
 * though the response shape is identical (mixed/featured mirror).
 * Reasons:
 *   - No cross-widget invalidation surprises (a mixed/featured refresh
 *     never touches the boost widget and vice versa).
 *   - The polling cadence, cooldown, and reapply hooks may diverge over
 *     time — coupling them now would force awkward branching later.
 *   - Cleaner v-if / getter names on the client (`getBoostFeaturedEvents`
 *     vs `getFeaturedEvents`) — the widget consumer never has to guess
 *     which "featured" store it's reading.
 *
 * Design decisions vs featuredEvents:
 *   - NO monotonic-shield / stale-payload guard — the boost widget
 *     doesn't drive live socket state, only carousel content, so a
 *     lagging payload is harmless (next poll fixes it).
 *   - NO 3-min cooldown — the widget polls at `suggestedPollMs` (60s
 *     from server meta) which is already a form of throttling.
 *   - NO KEY live-reapply — boost values are server-computed and
 *     shipped as final. The widget renders them verbatim.
 *
 * Endpoint contract:
 *   Response: {messages, data:[event], meta:{suggestedPollMs, hasMore, page}}
 *   Each `data[i]` has the same shape as mixed/featured — event + odds[]
 *   with one carousel-promoted market populated by the server. The widget
 *   is prematch-only (server derives the list from `boost_odds_rules_v3`
 *   filtered by context='prematch' + fixture status ≠ live/finished).
 */

const state = {
    events: [],
    loading: false,
    hasInitialFetch: false,
    lastFetchTime: null,
    // From response meta — the widget uses this to schedule polling
    // (default 60s if server omits it).
    suggestedPollMs: null,
    // Server-side pagination. `page` is the last page fetched, `hasMore`
    // tells the widget whether to trigger the next page on scroll.
    page: 1,
    hasMore: false,
};

const getters = {
    // Descriptive names — mirror the singular responsibility declared
    // at the top of the file. The widget imports these directly.
    getBoostFeaturedEvents: (state) => state.events,
    isLoadingBoostFeatured: (state) => state.loading,
    hasBoostFeaturedInitialFetch: (state) => state.hasInitialFetch,
    getBoostFeaturedSuggestedPollMs: (state) => state.suggestedPollMs,
    getBoostFeaturedPage: (state) => state.page,
    getBoostFeaturedHasMore: (state) => state.hasMore,
};

const mutations = {
    SET_LOADING(state, status) {
        state.loading = status;
    },
    SET_DATA(state, events) {
        state.events = Array.isArray(events) ? events : [];
        state.hasInitialFetch = true;
    },
    APPEND_DATA(state, events) {
        if (!Array.isArray(events) || events.length === 0) return;
        state.events = state.events.concat(events);
    },
    SET_META(state, meta) {
        if (!meta) return;
        state.suggestedPollMs = Number.isFinite(meta.suggestedPollMs) ? meta.suggestedPollMs : null;
        state.hasMore = meta.hasMore === true;
    },
    SET_PAGE(state, page) {
        state.page = Number.isFinite(page) && page > 0 ? page : 1;
    },
    SET_LAST_FETCH(state, ts) {
        state.lastFetchTime = ts;
    },
    RESET_DATA(state) {
        state.events = [];
        state.suggestedPollMs = null;
        state.page = 1;
        state.hasMore = false;
        // Deliberate: hasInitialFetch stays true. It's a "we've hit the
        // endpoint at least once" flag, not a "we have data" flag. The
        // widget uses it to distinguish first-load skeleton from
        // "confirmed empty" state.
    },
};

const actions = {
    /**
     * Fetch the boost-featured payload. Called on widget mount and by
     * the polling interval set up by the widget itself.
     *
     * @param {Object} payload
     * @param {string} [payload.lang='en']
     * @param {boolean} [payload.mobilePlatform=false]
     */
    async fetchBoostFeaturedEvents(context, payload) {
        const p = payload || {};
        const isInitialLoad = !context.state.hasInitialFetch;
        const append = p.append === true;
        const page = Number.isFinite(p.page) && p.page > 0 ? p.page : 1;

        // Show the skeleton loader only on the very first fetch — subsequent
        // background polls and load-more calls should be invisible.
        if (isInitialLoad) {
            context.commit('SET_LOADING', true);
        }

        const requestPayload = {
            lang: p.lang || 'en',
            mobilePlatform: p.mobilePlatform || false,
            platformId: context.rootGetters['settings/getPlatformId'],
            page,
        };

        const apiUrl = context.rootGetters['settings/getUrlApi'];
        const token = context.rootGetters['settings/getToken'];
        const url = `${apiUrl}/v3/events/mixed/boost-featured`;
        const headers = {Authorization: `Bearer ${token}`};

        try {
            const response = await axios.post(url, requestPayload, {headers});
            const data = response.data;

            context.commit('SET_LAST_FETCH', Date.now());
            context.commit('SET_META', data.meta || null);
            context.commit('SET_PAGE', page);

            const rows = (data && Array.isArray(data.data)) ? data.data : [];

            if (append) {
                // Append mode is only ever called with page > 1 by the
                // widget's load-more handler. An empty response here means
                // "server ran out" — we simply don't grow the list; meta
                // will carry hasMore:false so the widget stops asking.
                context.commit('APPEND_DATA', rows);
            } else if (rows.length > 0) {
                context.commit('SET_DATA', rows);
            } else {
                context.commit('RESET_DATA');
                // Explicitly mark initial-fetch=true so the skeleton
                // stops rendering after a confirmed-empty response.
                context.commit('SET_DATA', []);
            }
            return data.meta || null;
        } catch (error) {
            // Fail-open — the widget is a nice-to-have. On error we
            // reset to empty (only on the initial load) and stop the
            // loader; the next poll retries.
            console.error('[boostFeaturedEvents] fetch error:', error && error.message);
            if (isInitialLoad) {
                context.commit('RESET_DATA');
                context.commit('SET_DATA', []);
            }
            return null;
        } finally {
            if (isInitialLoad) {
                context.commit('SET_LOADING', false);
            }
        }
    },

    /**
     * Explicit reset (e.g. when the player switches skin / logout).
     * Clears both content and the initial-fetch flag so the next mount
     * gets the skeleton flash back.
     */
    resetBoostFeatured(context) {
        context.commit('RESET_DATA');
        context.commit('SET_LOADING', false);
        // Reset the initial-fetch flag by touching state directly via
        // a mutation — RESET_DATA intentionally leaves it alone.
        context.state.hasInitialFetch = false;
        context.state.lastFetchTime = null;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
